import React, { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Scheduler } from '@resideo/blueprint-react';
import { Button, ButtonIcon, Tooltip } from '@resideo/blueprint-oneportal';
import type { SchedulerEvent } from '@resideo/blueprint-react';
import type { RecordingBlock, DayOfWeek } from '../types';
import { generateBlockId, ALL_DAYS } from '../recordingScheduleUtils';
import AddRecordingBlockPopup from './AddRecordingBlockPopup';
import { chevronLeftIcon, informationLineIcon } from '@/assets/icons';
import styles from '../styled/Recording.module.css';
import {
  ScheduleEditorContainer,
  ScheduleEditorHeader,
  CameraNameTitle,
  HeaderSpacer,
  HeaderActions,
  CategoryTitle,
  LegendRow,
  LegendItem,
  LegendSwatchDefault,
  LegendSwatchOverride,
  LegendLabel,
  SchedulerWrapper,
  SubsectionCaption,
} from '../styled/Recording.styled';

interface ScheduleEditorProps {
  cameraName: string;
  blocks: RecordingBlock[];
  onBack: () => void;
  onSave: () => void;
  onCancel: () => void;
  hasChanges: boolean;
  isSaving: boolean;
  onAddBlock: (block: RecordingBlock) => void;
  onEditBlock: (block: RecordingBlock) => void;
  onDeleteBlock: (blockId: string) => void;
  disabled?: boolean;
}

const DAY_TO_RESOURCE: Record<DayOfWeek, string> = {
  mon: 'mon',
  tue: 'tue',
  wed: 'wed',
  thu: 'thu',
  fri: 'fri',
  sat: 'sat',
  sun: 'sun',
};

/** Map day abbreviation to a day offset from a Monday-start week */
const DAY_OFFSETS: Record<DayOfWeek, number> = {
  mon: 0,
  tue: 1,
  wed: 2,
  thu: 3,
  fri: 4,
  sat: 5,
  sun: 6,
};

// Arbitrary Monday-start reference week (Jan 2024) used to place recurring
// weekly blocks on the Scheduler's day columns — only the day-of-week matters.
const REFERENCE_WEEK_YEAR_MONTH = '2024-01';

// Blocks are edited exclusively via the add/edit popup, so drag-move and
// drag-resize on the Scheduler grid are intentionally disabled.
const SCHEDULER_EVENT_MOVE_HANDLING = 'Disabled';
const SCHEDULER_EVENT_RESIZE_HANDLING = 'Disabled';
// Dragging a time range opens the add-block popup pre-filled with that range.
const SCHEDULER_TIME_RANGE_SELECTED_HANDLING = 'Enabled';

// Scheduler event pill colors — passed to the Scheduler's `eventStyles` callback prop,
// which is the library's own styling API rather than a DOM inline style.
const SCHEDULER_EVENT_STYLES = {
  backgroundColor: 'rgba(0, 71, 220, 0.11)',
  borderColor: '#90a7dd',
  borderWidth: '1.5px',
  borderRadius: '8px',
  textColor: '#222f51',
};

/** Get display label for continuous recording mode */
function getContinuousLabel(enabled: boolean, t: (key: string) => string): string {
  return enabled ? t('common.enabled') : t('common.disabled');
}

/** Get display label for event recording mode */
function getEventLabel(clipEventRecording: boolean, snapshot: boolean, t: (key: string) => string): string {
  if (clipEventRecording && snapshot) return t('recording.eventRecordingNotificationsShort');
  if (!clipEventRecording && snapshot) return t('recording.snapshotOnly');
  return t('common.disabled');
}

/** Extract "H:MM" from a date-like value's "T HH:MM" portion, regardless of the date prefix's width */
function extractTimeLabel(dateLike: unknown): string {
  const match = String(dateLike).match(/T(\d{2}):(\d{2})/);
  if (!match) return '';
  const [, hour, minute] = match;
  return `${hour.replace(/^0/, '')}:${minute}`;
}

/**
 * Convert RecordingBlocks into SchedulerEvents for the Blueprint Scheduler.
 * Each block can span multiple days — we create one event per day per block.
 */
function blocksToEvents(blocks: RecordingBlock[], t: (key: string) => string): SchedulerEvent[] {
  const events: SchedulerEvent[] = [];

  for (const block of blocks) {
    for (const day of block.daysOfWeek) {
      const offset = DAY_OFFSETS[day];
      const dayStr = String(1 + offset).padStart(2, '0');
      const startTime = block.startTime.padStart(5, '0');
      const endTime = block.endTime.padStart(5, '0');

      const continuousLabel = getContinuousLabel(block.continuousRecording, t);
      const eventLabel = getEventLabel(block.clipEventRecording, block.snapshot, t);

      events.push({
        id: `${block.id}-${day}`,
        text: `${block.startTime} – ${block.endTime}`,
        start: `${REFERENCE_WEEK_YEAR_MONTH}-${dayStr}T${startTime}:00`,
        end: `${REFERENCE_WEEK_YEAR_MONTH}-${dayStr}T${endTime}:00`,
        resource: DAY_TO_RESOURCE[day],
        continuousRecording: block.continuousRecording,
        clipEventRecording: block.clipEventRecording,
        snapshot: block.snapshot,
        displayData: {
          continuousMode: `${t('recording.continuousRecordingModeShort')} ${continuousLabel}`,
          eventMode: `${t('recording.eventModeShort')} ${eventLabel}`,
        },
      });
    }
  }

  return events;
}

const ScheduleEditor: React.FC<ScheduleEditorProps> = ({
  cameraName,
  blocks,
  onBack,
  onSave,
  onCancel,
  hasChanges,
  isSaving,
  onAddBlock,
  onEditBlock,
  onDeleteBlock,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<RecordingBlock | null>(null);
  const [popupInitialValues, setPopupInitialValues] = useState<{ startTime?: string; endTime?: string; day?: DayOfWeek } | null>(null);
  const contextMenuEventRef = useRef<SchedulerEvent | null>(null);
  const contextMenuDayRef = useRef<DayOfWeek | null>(null);

  const events = useMemo(() => blocksToEvents(blocks, t), [blocks, t]);

  /** Custom event rendering to show time, continuous mode, and event mode per Figma */
  const handleBeforeEventRender = (args: any) => {
    const data = args.data as any;
    const startStr = extractTimeLabel(data.start);
    const endStr = extractTimeLabel(data.end);

    const continuousLabel = getContinuousLabel(data.continuousRecording, t);
    const eventLabel = getEventLabel(data.clipEventRecording, data.snapshot, t);

    data.html = `
      <div class="${styles.eventCard}">
        <div class="${styles.eventTime}">${startStr} – ${endStr}</div>
        <div class="${styles.eventModeRow}">
          <span class="${styles.eventModeRowLabel}">${t('recording.continuousRecordingModeShort')}</span><br/>
          <span class="${styles.eventModeRowValue}">${continuousLabel}</span>
        </div>
        <div class="${styles.eventModeRow}">
          <span class="${styles.eventModeRowLabel}">${t('recording.eventModeShort')}</span><br/>
          <span class="${styles.eventModeRowValue}">${eventLabel}</span>
        </div>
      </div>
    `;

    data.toolTip = `${startStr} – ${endStr}\n${t('recording.continuousRecordingModeShort')} ${continuousLabel}\n${t('recording.eventModeShort')} ${eventLabel}`;
  };

  /** Map day-of-week index (0=Sun … 6=Sat) to our DayOfWeek type */
  const INDEX_TO_DAY: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

  const handleTimeRangeSelected = (args: any) => {
    const start = new Date(args.start.toString());
    const end = new Date(args.end.toString());
    const startTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
    const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
    const day = INDEX_TO_DAY[start.getDay()];
    setEditingBlock(null);
    setPopupInitialValues({ startTime, endTime, day });
    setIsPopupOpen(true);
  };

  /** Find the original RecordingBlock from a SchedulerEvent id (format: "blockId-day") */
  const findBlockFromEventId = (eventId: string): RecordingBlock | null => {
    for (const block of blocks) {
      for (const day of block.daysOfWeek) {
        if (`${block.id}-${day}` === eventId) {
          return block;
        }
      }
    }
    return null;
  };

  /** Extract the day suffix from a SchedulerEvent id (format: "blockId-day") */
  const extractDayFromEventId = (eventId: string): DayOfWeek | null => {
    const parts = eventId.split('-');
    const day = parts[parts.length - 1] as DayOfWeek;
    return ALL_DAYS.includes(day) ? day : null;
  };

  /** Track which event was right-clicked */
  const handleEventContextMenu = (event: SchedulerEvent) => {
    contextMenuEventRef.current = event;
  };

  /** Called when "Edit Period" is selected from context menu */
  const handleEditPeriod = () => {
    const event = contextMenuEventRef.current;
    if (!event) return;
    const block = findBlockFromEventId(String(event.id));
    if (block) {
      const day = extractDayFromEventId(String(event.id));
      contextMenuDayRef.current = day;
      setEditingBlock(block);
      setPopupInitialValues(day ? { day } : null);
      setIsPopupOpen(true);
    }
    contextMenuEventRef.current = null;
  };

  const handleContextMenuClose = () => {
    contextMenuEventRef.current = null;
  };

  const handlePopupSave = (block: RecordingBlock) => {
    if (editingBlock) {
      const editedDay = contextMenuDayRef.current;

      // If editing a single day of a multi-day block, split the block:
      // keep the original block for unaffected days, and apply changes only to the edited day
      if (editedDay && editingBlock.daysOfWeek.length > 1) {
        const remainingDays = editingBlock.daysOfWeek.filter((d) => !block.daysOfWeek.includes(d));

        if (remainingDays.length > 0) {
          // Update original block to retain only the remaining days
          const originalWithRemainingDays: RecordingBlock = {
            ...editingBlock,
            daysOfWeek: remainingDays,
          };
          onEditBlock(originalWithRemainingDays);

          // Add a new block for the edited day(s) with the updated settings
          const newBlock: RecordingBlock = {
            ...block,
            id: generateBlockId(),
          };
          onAddBlock(newBlock);
        } else {
          // All days were selected in the popup — just replace the block entirely
          onEditBlock(block);
        }
      } else {
        onEditBlock(block);
      }
    } else {
      onAddBlock(block);
    }
    setIsPopupOpen(false);
    setEditingBlock(null);
    contextMenuDayRef.current = null;
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setEditingBlock(null);
    contextMenuDayRef.current = null;
  };

  const handleDeleteBlock = (blockId: string) => {
    const block = blocks.find((b) => b.id === blockId);
    const dayToRemove = contextMenuDayRef.current;

    // If block has multiple days and we know which day was right-clicked,
    // remove only that day instead of deleting the entire block
    if (block && dayToRemove && block.daysOfWeek.length > 1 && block.daysOfWeek.includes(dayToRemove)) {
      const updatedBlock: RecordingBlock = {
        ...block,
        daysOfWeek: block.daysOfWeek.filter((d) => d !== dayToRemove),
      };
      onEditBlock(updatedBlock);
    } else {
      onDeleteBlock(blockId);
    }

    contextMenuDayRef.current = null;
    setIsPopupOpen(false);
    setEditingBlock(null);
  };

  return (
    <ScheduleEditorContainer data-testid="schedule-editor">
      {/* Header: Back button + Camera Name + Save/Cancel */}
      <ScheduleEditorHeader>
        <ButtonIcon
          size="small"
          iconType="icon"
          onClick={onBack}
          aria-label={t('recording.backToSettings')}
          data-testid="schedule-editor-back"
        >
          <img src={chevronLeftIcon} width="20" height="20" alt="" aria-hidden="true" />
        </ButtonIcon>
        <CameraNameTitle>{cameraName} {t('recording.schedule')}</CameraNameTitle>
        <HeaderSpacer />
        <HeaderActions>
          <Button
            variant="secondary"
            size="small"
            onClick={onCancel}
            disabled={!hasChanges || isSaving}
            data-testid="schedule-cancel-btn"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={onSave}
            disabled={!hasChanges || isSaving}
            data-testid="schedule-save-btn"
          >
            {isSaving ? t('common.saving') : t('recording.saveSchedule')}
          </Button>
        </HeaderActions>
      </ScheduleEditorHeader>

      {/* Section title */}
      <div>
        <CategoryTitle>{t('recording.recordingSchedule')}</CategoryTitle>
        <SubsectionCaption>{t('recording.recordingScheduleCaption')}</SubsectionCaption>
      </div>

      {/* Legend + Action buttons */}
      <LegendRow>
        <LegendItem>
          <LegendSwatchDefault />
          <LegendLabel>{t('recording.cameraDefault')}</LegendLabel>
          <Tooltip text={t('recording.cameraDefaultTooltip')} place="bottom" event="hover">
            <img src={informationLineIcon} width={18} height={18} className={styles.legendInfoIcon} alt="" />
          </Tooltip>
        </LegendItem>
        <LegendItem>
          <LegendSwatchOverride />
          <LegendLabel>{t('recording.scheduledOverride')}</LegendLabel>
        </LegendItem>
      </LegendRow>

      {/* Blueprint Scheduler — weekly calendar */}
      <SchedulerWrapper>
        <Scheduler
          startDate="2024-01-01"
          weekStarts={1}
          events={events}
          businessBeginsHour={0}
          businessEndsHour={24}
          cellHeight={36}
          headerDateFormat="ddd"
          timeZone={timeZone}
          eventMoveHandling={SCHEDULER_EVENT_MOVE_HANDLING}
          eventResizeHandling={SCHEDULER_EVENT_RESIZE_HANDLING}
          timeRangeSelectedHandling={SCHEDULER_TIME_RANGE_SELECTED_HANDLING}
          allowEventOverlap={false}
          onTimeRangeSelected={handleTimeRangeSelected}
          onBeforeEventRender={handleBeforeEventRender}
          onEditPeriod={handleEditPeriod}
          onEventContextMenu={handleEventContextMenu}
          onContextMenuClose={handleContextMenuClose}
          isAddPeriodDisabled={disabled}
          eventStyles={() => SCHEDULER_EVENT_STYLES}
        />
      </SchedulerWrapper>

      {/* Add/Edit Recording Block Popup */}
      <AddRecordingBlockPopup
        isOpen={isPopupOpen}
        onClose={handlePopupClose}
        onSave={handlePopupSave}
        editBlock={editingBlock}
        onDelete={handleDeleteBlock}
        existingBlocks={blocks}
        initialValues={popupInitialValues}
      />
    </ScheduleEditorContainer>
  );
};

export default ScheduleEditor;
